import { useEffect } from 'react';
import { getProactiveMessages } from '../api';
import { addNudge, clearVisibleNudges } from './nudgeStore';

// 30s: every open console tab polls this independently, so the request rate is
// per-tab, not per-user. The visibilitychange listener re-polls on focus, so a
// longer interval costs responsiveness only for tabs the user is already
// looking at and not interacting with.
const POLL_INTERVAL_MS = 30000;
const DEFAULT_AGENT_ID = 'default-moo';

/**
 * Surface unread console-channel messages for the entity currently viewed, only
 * while on its detail page. Page-gated + entity-scoped (match on `entity_id`,
 * unique across types): other-entity and null-entity messages aren't shown, and
 * switching entities clears prior cards (handled-guard preserved). Dwell and
 * domain_event messages both stack.
 */
export function useProactiveNudgePoller(entityId: string | undefined): void {
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    clearVisibleNudges(); // prior entity's (or off-page) cards no longer apply
    if (!entityId) return undefined;

    let cancelled = false;
    const poll = async () => {
      if (document.visibilityState === 'hidden') return;
      try {
        const resp = await getProactiveMessages();
        if (cancelled) return;
        resp.data.messages
          .filter((m) => m.entity_id && m.entity_id === entityId)
          .forEach((m) => {
            addNudge({
              id: m.id,
              title: m.title || 'Moo has a suggestion',
              summary: m.summary,
              conversationId: m.conversation_id,
              agentId: m.agent_id || DEFAULT_AGENT_ID,
              taskId: m.task_id ?? '',
            });
          });
      } catch {
        // transient — next tick retries
      }
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') poll();
    };

    poll(); // surface anything already waiting for this entity
    const timer = setInterval(poll, POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [entityId]);
}
